import 'requestidlecallback-polyfill';
import { blinkConstants } from '@thoughtspot/blink-constants';
import { getPivotStatePropsFromDXPivotState } from '@thoughtspot/client-state-util';
import { CustomStylingConfig } from '@thoughtspot/custom-style-service';
import { isFetchPivotSummaryFromBackendEnabled } from '@thoughtspot/session-service';
import { getId, isAttribute, isMeasure, } from '@thoughtspot/visualization-column-util';
import chroma from 'chroma-js';
import _ from 'lodash';
import ReactDOM from 'react-dom';
import { areAxisConfigEqual } from '../../core/axis-config-util';
import { getAttributeColumns, getAxisConfig, getChartProperties, getChartSpecificProperties, getColumn, getPivotState, getPivotSummaryMode, getQueriesFromChartModel, getUseTreeLayout, getXAxisColumns, getYAxisColumns, hasOnlyAttributesInPivotValues, } from '../../core/chart-model-utils';
import { getQueryDefinitions, translateQueryDefinitionWithMeasureNamesValues, } from '../../core/chart-type-specification-service';
import { getConditionalFormats, isConditionSatisfied, } from '../../core/conditional-formatting-utils';
import { containsMeasureNamesOrMeasureValuesColumns } from '../../core/measure-names-and-values';
import { getEnvFlags } from '../../env';
import { ChartEventTypes } from '../../index';
import { AnswerColumnSortType, ChartType, ShowPivotSummaryPrior, } from '../../types';
import { createLogger } from '../../utils/logger';
import { areaToAxisMap, SummaryModesEnum, } from '../model/pivot-data-model';
import { COLUMN_EXPAND_PATH_KEY, FIELDS, generateQueriesForPivotExpandOperation, getKeyValueFromCellValue, ROW_EXPAND_PATH_KEY, SortBy, } from '../model/pivot-data-model-utils';
import { DATA } from '../pivot-context-menu.utils';
import { generateHyperlinkContainer } from './pivot.hyperlink.util';
import { ColumnControlButton } from './pivot-button';
export const fieldPanelSelector = 'dx-area-field-content';
export const fieldAreaSelector = 'dx-pivotgrid-fields-area';
export const rowHeaderLayoutClassName = 'pivot-row-header-layout-margin';
const logger = createLogger('pivot-component-utils');
const FONT_WEIGHT_BOLD = 'bold';
const FONT_STYLE_ITALIC = 'italic';
const TEXT_DECORATION_STRIKE_THROUGH = 'line-through';
const TEXT_DECORATION_UNDERLINE = 'underline';
export var ShowTotalsPrior;
(function (ShowTotalsPrior) {
    ShowTotalsPrior["BOTH"] = "both";
    ShowTotalsPrior["NONE"] = "none";
})(ShowTotalsPrior || (ShowTotalsPrior = {}));
export var Layout;
(function (Layout) {
    Layout["TREE"] = "tree";
    Layout["FLAT"] = "standard";
})(Layout || (Layout = {}));
export var MergePivotExportedRowsAndColumns;
(function (MergePivotExportedRowsAndColumns) {
    MergePivotExportedRowsAndColumns["OFF"] = "off";
    MergePivotExportedRowsAndColumns["MERGE_ROWS"] = "mergeRows";
    MergePivotExportedRowsAndColumns["MERGE_COLUMNS"] = "mergeColumns";
    MergePivotExportedRowsAndColumns["MERGE_ROWS_AND_COLUMNS"] = "mergeRowsAndColumns";
})(MergePivotExportedRowsAndColumns || (MergePivotExportedRowsAndColumns = {}));
export var PivotCellArea;
(function (PivotCellArea) {
    PivotCellArea["COLUMN"] = "column";
    PivotCellArea["DATA"] = "data";
    PivotCellArea["ROW"] = "row";
})(PivotCellArea || (PivotCellArea = {}));
export function supportsDownload() {
    return true;
}
export function setSize(pivotInstance) {
    if (!pivotInstance) {
        return;
    }
    pivotInstance.updateDimensions();
}
export function supportsFullScreenMode() {
    return false;
}
export function canOverlayHeatmap() {
    return true;
}
export const hasEmptyRowOrColumn = (axisConfig) => {
    return ((axisConfig.x.length > 0 && axisConfig.color.length === 0) ||
        (axisConfig.x.length === 0 && axisConfig.color.length > 0));
};
export const getShowTotalSummaries = (pivotDataModel, showTotals, showSummaries) => {
    if (containsMeasureNamesOrMeasureValuesColumns(getAxisConfig(pivotDataModel.chartModel)) &&
        showTotals === null) {
        return false;
    }
    if (hasEmptyRowOrColumn(getAxisConfig(pivotDataModel.chartModel))) {
        return true;
    }
    if (hasOnlyAttributesInPivotValues(pivotDataModel.chartModel)) {
        return false;
    }
    return showTotals !== null && showTotals !== void 0 ? showTotals : showSummaries;
};
export function updatePivotSettings(model, pivotInstance) {
    const showSummaries = false;
    const rowHeaderLayout = Layout.TREE;
    pivotInstance.option({
        showRowTotals: showSummaries,
        showColumnTotals: showSummaries,
        showRowGrandTotals: showSummaries,
        showColumnGrandTotals: showSummaries,
        rowHeaderLayout,
    });
}
export function fetchAndUpdateQueries(area, pathLength, { chartModel, pivotDataModel, pivotInstance }) {
    if (pivotDataModel.getIsLoading() === true) {
        return;
    }
    const dataSource = pivotInstance.current.instance.getDataSource();
    const queries = generateQueriesForPivotExpandOperation(area, pathLength, dataSource.state(), chartModel);
    if (queries.length === 0) {
        return;
    }
    pivotDataModel.setIsLoading(true);
    pivotDataModel.addQueriesToFetch(queries);
    dataSource.reload();
}
export function toggleHeaderExpansionState(fieldId, area, pathLength, isExpandOperation, { chartModel, pivotDataModel, pivotInstance }) {
    const dataSource = pivotInstance.current.instance.getDataSource();
    if (isExpandOperation) {
        dataSource.expandAll(fieldId);
    }
    else {
        dataSource.collapseAll(fieldId);
    }
}
export function toggleSelectionExpansionState(cellProperties, { chartModel, pivotDataModel, pivotInstance }, isExpandOperation, fetchPivotSummaryFromBackend) {
    if (!fetchPivotSummaryFromBackend) {
        return;
    }
    const dataSource = pivotInstance.current.instance.getDataSource();
    if (isExpandOperation) {
        dataSource.expandHeaderItem(cellProperties.area, cellProperties.cell.path);
    }
    else {
        dataSource.collapseHeaderItem(cellProperties.area, cellProperties.cell.path);
    }
}
export function sortOperationOnPivotGrid(sortField, path, sortByDataField, { pivotInstance }) {
    const dataSource = pivotInstance.current.instance.getDataSource();
    const field = dataSource.field(sortField);
    const sortOrder = field.sortOrder !== 'desc' ? 'desc' : 'asc';
    dataSource.field(sortField, {
        sortOrder,
        sortBySummaryPath: path,
        sortBySummaryField: sortByDataField,
        sortBy: SortBy.VALUE,
    });
    dataSource.reload();
}
export function clearSortOperation({ chartModel, pivotInstance, }) {
    const columns = getAttributeColumns(chartModel);
    const dataSource = pivotInstance.current.instance.getDataSource();
    columns.forEach(col => {
        dataSource.field(col.id, {
            sortOrder: null,
            sortBySummaryPath: null,
            sortBySummaryField: null,
            sortBy: SortBy.NONE,
        });
    });
    dataSource.reload();
}
function addRowHeaderMargin(pivotInstance, model) {
    const dataSource = pivotInstance.instance().getDataSource();
    return (!getUseTreeLayout(model) &&
        dataSource.getAreaFields(blinkConstants.pivotTable.Areas.DATA).length <
            2 &&
        dataSource.state().columnExpandedPaths.length === 0);
}
export function getOnCellPreparedCallback(cellPostProcessors, enableAttributesInPivotDataArea, isHtmlRenderingDisabled) {
    return params => {
        var _a, _b;
        params.cellElement.cell = params.cell;
        params.cellElement.columnIndex = params.columnIndex;
        params.cellElement.rowIndex = params.rowIndex;
        params.cellElement.area = params.area;
        if (enableAttributesInPivotDataArea) {
            if (params.area === 'data') {
                const fields = (_a = params.component
                    .instance()) === null || _a === void 0 ? void 0 : _a.getDataSource().getAreaFields(blinkConstants.pivotTable.Areas.DATA);
                const di = params.cell.dataIndex || 0;
                const column = fields === null || fields === void 0 ? void 0 : fields[di];
                if (column && column.isMeasure === false) {
                    const spanElements = params.cellElement.getElementsByTagName('span');
                    const spanElement = spanElements.length && spanElements[0];
                    let value = spanElement ? spanElement.innerText.trim() : '';
                    if (!_.isEmpty(value)) {
                        const cellValue = params.cell.text;
                        if (!_.isUndefined(cellValue)) {
                            if (!isHtmlRenderingDisabled) {
                                const { hyperlinkElement, title, } = generateHyperlinkContainer(cellValue);
                                if (hyperlinkElement) {
                                    params.cellElement.replaceChild(hyperlinkElement, spanElement);
                                }
                                value = title !== null && title !== void 0 ? title : value;
                            }
                            params.cellElement.setAttribute('title', value);
                            params.cellElement.classList.add('attribute');
                        }
                    }
                }
            }
            if (params.area === 'column' &&
                params.cell.type === 'GT' &&
                params.cell.text !== 'Summary') {
                Object.assign(params.cellElement.style, {
                    width: '150px',
                });
            }
        }
        if (params.area === 'row' || params.area === 'column') {
            const spanElements = params.cellElement.getElementsByTagName('span');
            const spanElement = spanElements.length && spanElements[spanElements.length - 1];
            const value = spanElement ? (_b = spanElement.innerText) === null || _b === void 0 ? void 0 : _b.trim() : '';
            if (!_.isEmpty(value)) {
                if (!isHtmlRenderingDisabled) {
                    const { hyperlinkElement } = generateHyperlinkContainer(value);
                    if (hyperlinkElement) {
                        params.cellElement.replaceChild(hyperlinkElement, spanElement);
                    }
                }
            }
        }
        cellPostProcessors.current.forEach(processor => processor(params));
    };
}
export function setupHeatmapStyles(pivotDataModel, model) {
    if (_.isEmpty(getAxisConfig(model).y)) {
        logger.error('Pivot Y Axis is empty. Invalid config', model);
    }
    const heatmapStyles = getAxisConfig(model).y.reduce((styles, col, idx) => {
        if (isAttribute(col)) {
            return styles;
        }
        const colId = getId(col);
        const min = pivotDataModel.min(colId);
        const max = pivotDataModel.max(colId);
        const heatmapColor = CustomStylingConfig.getPivotHeatmapColor(idx);
        const hcScale = chroma
            .scale(['white', heatmapColor])
            .padding([0.1, -0.3]);
        styles[colId] =
            max === min
                ? value => {
                    return hcScale(0);
                }
                : value => {
                    if (getPivotSummaryMode(model) !== SummaryModesEnum.None) {
                        return hcScale(value);
                    }
                    let val = (0.7 * (value - min)) / (max - min);
                    if (val > 1) {
                        val = 1;
                    }
                    return hcScale(val);
                };
        return styles;
    }, {});
    return heatmapStyles;
}
export const getPivotOptions = (props) => {
    const { isPrintMode, chartOptions, pivotDataModel, getTranslatedText, showSummaries, rowHeaderLayout, sumType, dataFieldArea, isContextMenuEnabledOnLeftClick, enableAttributesInPivotDataArea, showPivotSummaryPrior, showRowTotals, showColumnTotals, showRowGrandTotals, showColumnGrandTotals, } = props;
    let onlyAttributesInPivotValues = false;
    if (enableAttributesInPivotDataArea) {
        onlyAttributesInPivotValues = hasOnlyAttributesInPivotValues(pivotDataModel.chartModel);
    }
    return {
        elementAttr: {
            class: rowHeaderLayout === Layout.FLAT
                ? 'pivot-inner flat-layout'
                : 'pivot-inner',
        },
        allowSortingBySummary: !chartOptions.disableTransformations &&
            !isContextMenuEnabledOnLeftClick,
        allowSorting: false,
        allowFiltering: false,
        allowExpandAll: onlyAttributesInPivotValues || !isContextMenuEnabledOnLeftClick,
        dataFieldArea,
        loadPanel: {
            enabled: !isPrintMode,
        },
        showRowTotals: getShowTotalSummaries(pivotDataModel, showRowTotals, showSummaries),
        showColumnTotals: getShowTotalSummaries(pivotDataModel, showColumnTotals, showSummaries),
        showRowGrandTotals: getShowTotalSummaries(pivotDataModel, showRowGrandTotals, showSummaries),
        showColumnGrandTotals: getShowTotalSummaries(pivotDataModel, showColumnGrandTotals, showSummaries),
        showTotalsPrior: showPivotSummaryPrior === ShowPivotSummaryPrior.Both
            ? ShowTotalsPrior.BOTH
            : ShowTotalsPrior.NONE,
        hideEmptySummaryCells: false,
        height: '100%',
        export: {
            enabled: false,
        },
        texts: {
            collapseAll: getTranslatedText('pivotTable.COLLAPSE_ALL'),
            expandAll: getTranslatedText('pivotTable.EXPAND_ALL'),
            noData: getTranslatedText('NO_DATA'),
            removeAllSorting: getTranslatedText('pivotTable.REMOVE_ALL_SORTING'),
            sortColumnBySummary: getTranslatedText('pivotTable.SORT_COLUMN', {
                skipInterpolation: true,
            }),
            sortRowBySummary: getTranslatedText('pivotTable.SORT_ROW', {
                skipInterpolation: true,
            }),
            total: onlyAttributesInPivotValues
                ? label => label
                : getTranslatedText('pivotTable.TOTAL', {
                    skipInterpolation: true,
                }),
            grandTotal: sumType,
        },
        rowHeaderLayout,
        dataSource: pivotDataModel.getPivotDataSource(),
    };
};
export const getTextDecoration = (fontProperties) => {
    if (fontProperties === null || fontProperties === void 0 ? void 0 : fontProperties.strikeThrough)
        return TEXT_DECORATION_STRIKE_THROUGH;
    if (fontProperties === null || fontProperties === void 0 ? void 0 : fontProperties.underline)
        return TEXT_DECORATION_UNDERLINE;
    return '';
};
const getHeatmapColor = (heatmapStyles, columnId, value) => {
    if (heatmapStyles.current[columnId]) {
        try {
            return heatmapStyles.current[columnId](value).css();
        }
        catch (e) {
            logger.warn('getHeatmapColor', 'Failed to apply heatmap style', e);
        }
    }
    return '';
};
export const heatmapCellFormatter = (pivotInstance, heatmapStyles, viz) => {
    return params => {
        var _a;
        let value = params.cell.value;
        const fields = params.component
            .instance()
            .getDataSource()
            .getAreaFields(blinkConstants.pivotTable.Areas.DATA);
        if (_.isNumber(value)) {
            const di = params.cell.dataIndex || 0;
            const colId = (_a = fields === null || fields === void 0 ? void 0 : fields[di]) === null || _a === void 0 ? void 0 : _a.dataField;
            if (!colId) {
                logger.warn('heatMapCellFormatter', 'colId should not be empty');
                return;
            }
            const color = getHeatmapColor(heatmapStyles, colId, value);
            params.cellElement.style.backgroundColor = color;
        }
        else if (_.isString(value)) {
            const containsMeasureNames = containsMeasureNamesOrMeasureValuesColumns(getAxisConfig(viz));
            if (containsMeasureNames) {
                const { key, value: val } = getKeyValueFromCellValue(value);
                const columns = getAxisConfig(viz).y;
                const idx = key % columns.length;
                if (!_.isNil(key) && !_.isNil(val) && !_.isNil(columns[idx])) {
                    const columnId = getId(columns[key % columns.length]);
                    const color = getHeatmapColor(heatmapStyles, columnId, val);
                    params.cellElement.style.backgroundColor = color;
                }
            }
        }
    };
};
export function getCounts(model, pivotDataModel, rowIndex, columnIndex) {
    var _a, _b, _c, _d;
    const configRowsCount = getXAxisColumns(model).length;
    const configValuesCount = getYAxisColumns(model).length;
    const pivotRowsCount = pivotDataModel.getPivotRowsCount() ||
        ((_b = (_a = model.data) === null || _a === void 0 ? void 0 : _a[2]) === null || _b === void 0 ? void 0 : _b.columnDataLite[0].dataValue.length) * configRowsCount ||
        0;
    const pivotColumnsCount = ((_d = (_c = model.data) === null || _c === void 0 ? void 0 : _c[3]) === null || _d === void 0 ? void 0 : _d.columnDataLite[0].dataValue.length) *
        configValuesCount || 0;
    const dataRowIndex = pivotRowsCount * Math.floor(columnIndex / configValuesCount) +
        Math.floor(rowIndex / configRowsCount);
    return {
        dataRowIndex,
        pivotRowsCount,
        pivotColumnsCount,
    };
}
export function setupConditionalFormattingStyles(pivotDataModel, model) {
    if (_.isEmpty(getAxisConfig(model).y)) {
        logger.error('Pivot Y Axis is empty. Invalid config', model);
    }
    const conditionalFormatingStyles = getAxisConfig(model).y.reduce((styles, col, idx) => {
        const columnId = getId(col);
        const formats = getConditionalFormats(model.vizProp, columnId, 'ChartVizProps', model);
        const data = model === null || model === void 0 ? void 0 : model.data[0];
        styles[columnId] = (value, rowIndex, columnIndex) => {
            var _a, _b;
            let backgroundColor = '';
            let fontColor = '';
            let fontWeight = '';
            let fontStyle = '';
            let textDecoration = '';
            for (let index = 0; index < formats.length; index++) {
                const rule = formats[index];
                let lhsColValue;
                let rhsColValue;
                const isSummary = false;
                if (isConditionSatisfied(rule, value, model.inScopeParameters, lhsColValue, rhsColValue)) {
                    const { solidBackgroundAttrs, fontProperties, } = formats[index];
                    backgroundColor = (_a = solidBackgroundAttrs === null || solidBackgroundAttrs === void 0 ? void 0 : solidBackgroundAttrs.color) !== null && _a !== void 0 ? _a : '';
                    fontColor = (_b = fontProperties === null || fontProperties === void 0 ? void 0 : fontProperties.color) !== null && _b !== void 0 ? _b : '';
                    fontWeight = (fontProperties === null || fontProperties === void 0 ? void 0 : fontProperties.bold)
                        ? FONT_WEIGHT_BOLD
                        : '';
                    fontStyle = (fontProperties === null || fontProperties === void 0 ? void 0 : fontProperties.italic)
                        ? FONT_STYLE_ITALIC
                        : '';
                    textDecoration = getTextDecoration(fontProperties);
                    break;
                }
            }
            return {
                backgroundColor,
                fontColor,
                fontWeight,
                fontStyle,
                textDecoration,
            };
        };
        return styles;
    }, {});
    return conditionalFormatingStyles;
}
export const conditionalCellFormatter = (pivotInstance, conditionalFormatingStyles, viz) => {
    return params => {
        var _a, _b;
        if (params.area === DATA) {
            let value = params.cell.value;
            const fields = (_a = params.component
                .instance()) === null || _a === void 0 ? void 0 : _a.getDataSource().getAreaFields(blinkConstants.pivotTable.Areas.DATA);
            const di = params.cell.dataIndex || 0;
            let columnId = (_b = fields === null || fields === void 0 ? void 0 : fields[di]) === null || _b === void 0 ? void 0 : _b.dataField;
            const containsMeasureNames = containsMeasureNamesOrMeasureValuesColumns(getAxisConfig(viz));
            if (containsMeasureNames) {
                const { key, value: val } = getKeyValueFromCellValue(value);
                if (!_.isNil(key) && !_.isNil(val)) {
                    const columns = getAxisConfig(viz).y;
                    columnId = getId(columns[key % columns.length]);
                    value = val;
                }
            }
            if (!columnId) {
                logger.warn('conditionalCellFormatter', 'columnId should not be empty');
                return;
            }
            const { backgroundColor, fontColor, fontWeight, fontStyle, textDecoration, } = conditionalFormatingStyles.current[columnId]
                ? conditionalFormatingStyles.current[columnId](value, params.rowIndex, params.columnIndex)
                : params.cellElement.style;
            params.cellElement.style.backgroundColor = backgroundColor;
            params.cellElement.style.color = fontColor;
            params.cellElement.style.fontWeight = fontWeight;
            params.cellElement.style.fontStyle = fontStyle;
            params.cellElement.style.textDecoration = textDecoration;
        }
    };
};
export function getParsedPivotStateFromModel(chartModel, dxPivotState) {
    const pivotState = getPivotState(chartModel);
    if (pivotState === null) {
        return {};
    }
    return {
        [ROW_EXPAND_PATH_KEY]: pivotState.rowExpandedPaths
            ? _.map(pivotState.rowExpandedPaths, (path) => path.paths)
            : [],
        [COLUMN_EXPAND_PATH_KEY]: pivotState.columnExpandedPaths
            ? _.map(pivotState.columnExpandedPaths || [], (path) => path.paths)
            : [],
        [FIELDS]: !_.isNil(dxPivotState.fields) ? dxPivotState.fields : [],
    };
}
function getPivotQueryDefinitions(model, axisConfig) {
    const queries = getQueryDefinitions(ChartType.PivotTable, axisConfig, model);
    return translateQueryDefinitionWithMeasureNamesValues(queries, model, ChartType.PivotTable);
}
async function reloadDataIfRequired(model, pivotInstance, pivotDataModel, isUserAction = true) {
    const chartModelQueries = getQueriesFromChartModel(model).map(query => {
        const updatedQuery = {
            ...query,
        };
        delete updatedQuery.__typename;
        delete updatedQuery.category;
        delete updatedQuery.size;
        delete updatedQuery.hidden;
        delete updatedQuery.sort;
        return updatedQuery;
    });
    const requiredQueries = getPivotQueryDefinitions(model, getAxisConfig(model));
    if (chartModelQueries.length === requiredQueries.length &&
        _.isEqual(chartModelQueries, requiredQueries)) {
        pivotInstance.endUpdate();
        return;
    }
    pivotDataModel.setIsLoading(true);
    pivotDataModel.addQueriesToFetch(requiredQueries);
    const dataSource = pivotInstance.getDataSource();
    const parsedPivotState = getParsedPivotStateFromModel(model, dataSource.state());
    await pivotDataModel.loadDataSource(parsedPivotState, null, isUserAction);
    pivotInstance.endUpdate();
    dataSource.reload();
}
export function onInitialized(model, pivotDataModel) {
    return async function (params) {
        const pivotInstance = params.component;
        const dataSource = pivotInstance.getDataSource();
        const parsedPivotState = getParsedPivotStateFromModel(model, dataSource.state());
        pivotInstance.beginUpdate();
        dataSource === null || dataSource === void 0 ? void 0 : dataSource.state(parsedPivotState);
        await reloadDataIfRequired(model, pivotInstance, pivotDataModel, false);
    };
}
export function sortByHeaderMenu({ dxPivotInstance, column, sortType, model, }) {
    const columnId = getId(column);
    const dataSource = dxPivotInstance.instance().getDataSource();
    if (isMeasure(column)) {
        dataSource.fields().forEach((field) => {
            if (field.area === 'row' || field.area === 'column') {
                dataSource.field(field.dataField, {
                    sortOrder: sortType === AnswerColumnSortType.Ascending
                        ? 'asc'
                        : 'desc',
                    sortBySummaryField: column.id,
                    sortBySummaryPath: [],
                    sortBy: SortBy.VALUE,
                });
            }
        });
        dataSource.reload();
    }
    if (!isMeasure(column)) {
        dataSource.fields().forEach((field) => {
            if (field.dataField === columnId) {
                dataSource.field(columnId, {
                    sortOrder: sortType === AnswerColumnSortType.Ascending
                        ? 'asc'
                        : 'desc',
                    sortBySummaryField: columnId,
                    sortBySummaryPath: [],
                    sortBy: SortBy.VALUE,
                });
            }
        });
        dataSource.reload();
    }
    const updatedClientState = _.assign({}, model.vizProp, {
        chartProperties: {
            ...getChartProperties(model),
            chartSpecific: {
                ...getChartSpecificProperties(model),
                pivotState: getPivotStatePropsFromDXPivotState(dataSource.state()),
            },
        },
    });
    return updatedClientState;
}
function executeEventHandler(eventsMap, eventType, args = []) {
    const eventHandler = eventsMap.get(eventType);
    if (_.isFunction(eventHandler)) {
        return eventHandler.call(null, ...args);
    }
    logger.warn(`Chart Event Callback not implemented for ${eventType}.`);
    return undefined;
}
export function updateChartProperties(pivotDataModel, vizProp, eventsMap, shouldRender = false) {
    executeEventHandler(eventsMap, ChartEventTypes.UpdateClientState, [
        vizProp,
        shouldRender,
        !pivotDataModel.getClientStateUpdatedBySystemAction(),
    ]);
    pivotDataModel.setClientStateUpdatedBySystemAction(true);
}
export const isUpdateAxisConfigWithDataFetchNeeded = (updatedModel) => {
    const chartModelQueries = getQueriesFromChartModel(updatedModel);
    const requiredQueries = getPivotQueryDefinitions(updatedModel, getAxisConfig(updatedModel));
    return chartModelQueries.length < requiredQueries.length;
};
export const isUpdateAxisConfigWithoutDataFetchNeeded = (oldChartModel, newChartModel, chartConfig) => {
    let queries = getQueriesFromChartModel(oldChartModel);
    if (!isFetchPivotSummaryFromBackendEnabled()) {
        queries = getPivotQueryDefinitions(oldChartModel, chartConfig);
    }
    const updatedQueries = getPivotQueryDefinitions(newChartModel, chartConfig);
    return (!_.isEqual(queries, updatedQueries) &&
        queries.length > updatedQueries.length);
};
export const isPivotHeaderExpanded = (dxPivotState) => {
    var _a, _b;
    return (!!((_a = dxPivotState.columnExpandedPaths) === null || _a === void 0 ? void 0 : _a.length) ||
        !!((_b = dxPivotState.rowExpandedPaths) === null || _b === void 0 ? void 0 : _b.length));
};
export const isClientStateUpgradeNeeded = (chartModel, dxPivotState) => {
    const currPivotState = getPivotState(chartModel);
    const derivedDxPivotState = getPivotStatePropsFromDXPivotState(dxPivotState);
    if (!currPivotState) {
        const result = isPivotHeaderExpanded(dxPivotState) ||
            !!derivedDxPivotState.fields.length;
        if (!result) {
            return false;
        }
    }
    return _.reduce(_.keys(dxPivotState), (needUpdate, key) => {
        return (needUpdate ||
            !_.isEqual(_.get(currPivotState, key, undefined), _.get(derivedDxPivotState, key, undefined)));
    }, false);
};
const generateVisualAxisConfigFromFields = (fields, model) => {
    const oldChartConfig = getAxisConfig(model);
    const axisConfig = fields.reduce((config, field) => {
        const targetAxis = areaToAxisMap[field.area];
        if (!_.isNil(targetAxis)) {
            config[targetAxis].push(getColumn(model, field.dataField));
        }
        return config;
    }, {
        x: [],
        y: [],
        color: [],
    });
    const containsMeasureNamesColumns = containsMeasureNamesOrMeasureValuesColumns(axisConfig);
    const newChartConfig = {
        x: axisConfig.x,
        y: containsMeasureNamesColumns
            ? model.config.axisConfig[0].measureValues
            : axisConfig.y,
        color: axisConfig.color,
        sort: oldChartConfig.sort,
    };
    return newChartConfig;
};
export function updateState(model, pivotInstance, eventsMap, options, pivotDataModel) {
    const dataSource = pivotInstance.instance().getDataSource();
    const fields = _.sortBy(dataSource.fields(), 'areaIndex');
    let needsUpdate = false;
    let axisConfigChanged = false;
    const newChartConfig = generateVisualAxisConfigFromFields(fields, model);
    if (!areAxisConfigEqual(getAxisConfig(model), newChartConfig)) {
        axisConfigChanged = true;
        needsUpdate = true;
    }
    const state = dataSource.state();
    if (state.useTreeLayout !== getUseTreeLayout(model)) {
        needsUpdate = true;
    }
    const needClientStateUpgrade = isClientStateUpgradeNeeded(model, state);
    const updatedClientState = needClientStateUpgrade
        ? _.assign({}, model.vizProp, {
            chartProperties: {
                ...getChartProperties(model),
                chartSpecific: {
                    ...getChartSpecificProperties(model),
                    pivotState: getPivotStatePropsFromDXPivotState(state),
                },
            },
        })
        : model.vizProp;
    const updatedModel = {
        ...model,
        vizProp: updatedClientState,
    };
    const shouldUpdateConfigWithDataFetch = isUpdateAxisConfigWithDataFetchNeeded(updatedModel);
    const shouldUpdateConfigWithoutDataFetch = isUpdateAxisConfigWithoutDataFetchNeeded(model, updatedModel, newChartConfig);
    if (needsUpdate) {
    }
    if (axisConfigChanged && !options.disableTransformations) {
        const newClientState = _.assign({}, model.vizProp, {
            chartProperties: {
                ...getChartProperties(model),
                chartSpecific: {
                    ...getChartSpecificProperties(model),
                    pivotState: null,
                },
            },
        });
        eventsMap.get(ChartEventTypes.UpdateAxisConfigAndClientState)(getPivotQueryDefinitions(model, newChartConfig), newClientState, [], true);
    }
    else if (needClientStateUpgrade && shouldUpdateConfigWithDataFetch) {
        pivotDataModel.setIsLoading(true);
        const requiredQueries = getPivotQueryDefinitions(updatedModel, getAxisConfig(updatedModel));
        const queriesToFetch = pivotDataModel.addQueriesToFetch(requiredQueries);
        const parsedPivotState = getParsedPivotStateFromModel(updatedModel, state);
        pivotDataModel.loadDataSource(parsedPivotState, null, false);
        if (queriesToFetch.length === 0 ||
            !getEnvFlags().fetchPivotSummaryFromBackend) {
            updateChartProperties(pivotDataModel, updatedClientState, eventsMap);
        }
    }
    else if (shouldUpdateConfigWithoutDataFetch) {
        const queryDefinitions = getPivotQueryDefinitions(updatedModel, newChartConfig);
        eventsMap.get(ChartEventTypes.UpdateAxisConfigAndClientState)(queryDefinitions, updatedClientState, [], false, false);
    }
    else if (needClientStateUpgrade) {
        logger.info('Is client state updated due to user action ', !pivotDataModel.getClientStateUpdatedBySystemAction());
        updateChartProperties(pivotDataModel, updatedClientState, eventsMap);
        return true;
    }
    else {
        return true;
    }
}
export function onContentReady(model, eventsMap, options = {}, pivotDataModel) {
    let currentRenderCompleteIdleCallback = null;
    let hasScheduledRepaint = false;
    const clearIdleCallback = () => {
        if (currentRenderCompleteIdleCallback)
            window.cancelIdleCallback(currentRenderCompleteIdleCallback);
        currentRenderCompleteIdleCallback = null;
    };
    return (params) => {
        var _a, _b, _c;
        clearIdleCallback();
        if (pivotDataModel.getIsLoading() === true) {
            return;
        }
        const pivotInstance = params.component;
        const onChartRenderComplete = eventsMap.get(ChartEventTypes.RenderComplete);
        if (options.disableTransformations) {
            onChartRenderComplete(model);
            return;
        }
        const currentPivotInstance = pivotInstance;
        if (addRowHeaderMargin(currentPivotInstance, model)) {
            params.element.classList.add(rowHeaderLayoutClassName);
        }
        else {
            params.element.classList.remove(rowHeaderLayoutClassName);
        }
        const rowCount = ((_c = (_b = (_a = params.element
            .querySelector('.dx-pivotgrid-vertical-headers')) === null || _a === void 0 ? void 0 : _a.querySelector('table')) === null || _b === void 0 ? void 0 : _b.rows) === null || _c === void 0 ? void 0 : _c.length) || null;
        pivotDataModel.setPivotRowsCount(rowCount);
        const fields = pivotInstance
            .instance()
            .getDataSource()
            .fields();
        const fieldAreaElems = [
            ...params.element.getElementsByClassName(fieldAreaSelector),
        ];
        _.each(fieldAreaElems, (fieldAreaElement, idx) => {
            const group = fieldAreaElement.getAttribute('group');
            if (group === 'filter')
                return;
            const fieldPanelElements = [
                ...fieldAreaElement.getElementsByClassName(fieldPanelSelector),
            ];
            _.each(fieldPanelElements, (panelElem, fieldIndex) => {
                const matchingField = fields.find(field => field.area === group && field.areaIndex === fieldIndex);
                const vizColumn = getColumn(model, matchingField.dataField);
                const newReactElem = ColumnControlButton({
                    onClickHandler: evt => {
                        evt.persist();
                        eventsMap.get(ChartEventTypes.PivotColumnClick)(evt, getId(vizColumn), (sortType) => {
                            return sortByHeaderMenu({
                                dxPivotInstance: pivotInstance.instance(),
                                column: vizColumn,
                                sortType,
                                model,
                            });
                        });
                    },
                });
                const newElem = document.createElement('div');
                panelElem.parentElement.append(newElem);
                panelElem.parentElement.setAttribute('class', `${panelElem.parentElement.getAttribute('class')} bkv2-interactive`);
                ReactDOM.render(newReactElem, newElem);
            });
        });
        setSize(currentPivotInstance);
        const updateComplete = updateState(model, currentPivotInstance, eventsMap, options, pivotDataModel);
        if (!hasScheduledRepaint) {
            hasScheduledRepaint = true;
            setTimeout(() => {
                try {
                    currentPivotInstance.instance().repaint();
                }
                catch (e) {
                }
            }, 200);
        }
        if (getEnvFlags().isDynamicColumnWidthForPivotEnabled) {
            const vizId = model.id;
            const answerNode = document.querySelector(`div[data-vizid="${vizId}"]`);
            if (answerNode instanceof HTMLDivElement) {
                const chartarea = answerNode.getElementsByClassName('chart-area')[0];
                const pivotTable = answerNode.getElementsByClassName('pivot-table')[0];
                const tableInner = answerNode.getElementsByClassName('pivot-inner')[0];
                const dataHeader = params.element.getElementsByClassName('dx-data-header')[0];
                const areaRowCell = params.element.getElementsByClassName('dx-area-row-cell')[0];
                const areaDataCell = params.element.getElementsByClassName('dx-area-data-cell')[0];
                const pivotGridFieldsArea = params.element.querySelector('.dx-pivotgrid .dx-area-description-cell .dx-pivotgrid-fields-area');
                if (chartarea &&
                    pivotTable &&
                    tableInner &&
                    dataHeader &&
                    areaRowCell &&
                    areaDataCell &&
                    pivotGridFieldsArea) {
                    areaRowCell.style.maxWidth = 'none';
                    areaDataCell.style.maxWidth = 'none';
                    dataHeader.style.maxWidth = 'none';
                    pivotGridFieldsArea.style.maxWidth = 'none';
                    chartarea.style.display = 'block';
                    chartarea.style.overflowX = 'auto';
                    pivotTable.style.width = 'max-content';
                    pivotTable.style.minWidth = '100%';
                    pivotTable.style.position = 'initial';
                    tableInner.style.position = 'initial';
                }
            }
        }
        if (updateComplete) {
            currentRenderCompleteIdleCallback = window.requestIdleCallback(() => {
                onChartRenderComplete(model);
            }, { timeout: 100 });
        }
    };
}
export const replaceSpecialCharToUnderscore = fileName => {
    return fileName.replace(/[\W_]/g, '_');
};
export const shouldMergeFieldValue = (area, mergePivotExportedRowsAndColumns) => {
    const mergeState = _.isNil(mergePivotExportedRowsAndColumns) ||
        mergePivotExportedRowsAndColumns ===
            MergePivotExportedRowsAndColumns.MERGE_ROWS_AND_COLUMNS;
    if (mergeState)
        return mergeState;
    if (area === PivotCellArea.ROW) {
        return (mergePivotExportedRowsAndColumns ===
            MergePivotExportedRowsAndColumns.MERGE_ROWS);
    }
    if (area === PivotCellArea.COLUMN) {
        return (mergePivotExportedRowsAndColumns ===
            MergePivotExportedRowsAndColumns.MERGE_COLUMNS);
    }
    return false;
};
export function updateAxisConfigsOnTogglingSummaries(chartModel, updatedVizProp, pivotDataModel, eventsMap) {
    const updatedChartModel = {
        ...chartModel,
        vizProp: updatedVizProp,
    };
    const oldChartModelQueries = getQueriesFromChartModel(updatedChartModel);
    const newUpdatedQueries = getPivotQueryDefinitions(updatedChartModel, getAxisConfig(updatedChartModel));
    if (oldChartModelQueries.length < newUpdatedQueries.length) {
        pivotDataModel.setIsLoading(true);
        pivotDataModel.addQueriesToFetch(newUpdatedQueries);
        const state = pivotDataModel.pivotInstance.current.instance
            .getDataSource()
            .state();
        const parsedPivotState = getParsedPivotStateFromModel(updatedChartModel, state);
        pivotDataModel.loadDataSource(parsedPivotState, updatedChartModel);
    }
    else if (oldChartModelQueries.length > newUpdatedQueries.length) {
        const queryDefinitions = getPivotQueryDefinitions(updatedChartModel, getAxisConfig(updatedChartModel));
        eventsMap.get(ChartEventTypes.UpdateAxisConfigAndClientState)(queryDefinitions, updatedChartModel.vizProp, [], false, false);
    }
    else {
        updateChartProperties(pivotDataModel, updatedChartModel.vizProp, eventsMap, true);
    }
}
export const testingExports = {
    reloadDataIfRequired,
    getPivotQueryDefinitions,
    generateVisualAxisConfigFromFields,
};
//# sourceMappingURL=pivot.component.utils.js.map